// Navigation + modal state for the unlocked app, and the "perspective" helper
// that filters data to what the current viewer (owner, client, or owner
// previewing as a client) is allowed to see.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { expiryInfo } from "../lib/format";
import type { Category, CredRequest, Item, Perm, Vault } from "../lib/types";
import { useVault } from "../state/store";

export type View = "home" | "vault" | "clients" | "expiring" | "gen" | "settings";
export type Tab = "items" | "requests" | "access";

export interface Nav {
  view: View;
  vaultId: string | null;
  tab: Tab;
  /** null = category tiles home; "all"; "__none" (uncategorised); "__arch"; or a category id */
  cat: string | null;
  itemId: string | null;
  pane: "list" | "detail";
  q: string;
}

export type Modal =
  | { kind: "item"; itemId: string | null; vaultId?: string; catId?: string; preset?: { type: "login"; password: string } }
  | { kind: "share"; tab: "invite" | "link"; itemId: string | null }
  | { kind: "request" }
  | { kind: "codes" }
  | { kind: "client" }
  | { kind: "cat" }
  | { kind: "delcat"; catId: string }
  | { kind: "password" }
  | { kind: "totp" }
  | { kind: "export" }
  | { kind: "confirm-delete"; itemId: string }
  | { kind: "invite-link"; email: string; url: string; vaultName: string };

interface UI {
  nav: Nav;
  go: (patch: Partial<Nav>) => void;
  openVault: (id: string, patch?: Partial<Nav>) => void;
  modal: Modal | null;
  setModal: (m: Modal | null) => void;
}

const Ctx = createContext<UI | null>(null);
export const useUI = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useUI outside UIProvider");
  return v;
};

export function UIProvider({ children, initialVault }: { children: ReactNode; initialVault: string | null }) {
  const [nav, setNav] = useState<Nav>({ view: "vault", vaultId: initialVault, tab: "items", cat: null, itemId: null, pane: "list", q: "" });
  const [modal, setModal] = useState<Modal | null>(null);
  const go = useCallback((patch: Partial<Nav>) => setNav((n) => ({ ...n, ...patch })), []);
  const openVault = useCallback(
    (id: string, patch: Partial<Nav> = {}) =>
      setNav({ view: "vault", vaultId: id, tab: "items", cat: null, itemId: null, pane: "list", q: "", ...patch }),
    [],
  );
  const value = useMemo(() => ({ nav, go, openVault, modal, setModal }), [nav, go, openVault, modal]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** What the current viewer sees, and may do, in each vault. */
export function usePerspective() {
  const { data, me, isOwner, preview } = useVault();
  return useMemo(() => {
    const asClient = !isOwner || !!preview;
    const permOf = (v: Vault): Perm => (preview && v.id === preview.vaultId ? preview.perm : v.perm);
    const vaults = preview ? data.vaults.filter((v) => v.id === preview.vaultId) : data.vaults;
    const viewerId = preview ? preview.userId : me.id;

    const canSee = (v: Vault) => permOf(v) !== "add";
    const canAdd = (v: Vault) => permOf(v) !== "view" && !!v.key;
    const canEdit = (v: Vault) => permOf(v) === "owner" || permOf(v) === "edit";

    const visibleItems = (v: Vault): Item[] =>
      data.items.filter(
        (i) =>
          i.vaultId === v.id &&
          (!asClient || (!i.private && !i.archivedAt && (canSee(v) || i.createdBy === viewerId))),
      );
    const liveItems = (v: Vault) => visibleItems(v).filter((i) => !i.archivedAt);
    const cats = (v: Vault): Category[] => data.categories.filter((c) => c.vaultId === v.id);
    const requests = (v: Vault): CredRequest[] =>
      data.requests.filter((r) => r.vaultId === v.id && (!asClient || (r.toUserId === viewerId && r.status !== "cancelled")));
    const waiting = (v: Vault) => requests(v).filter((r) => r.status === "waiting").length;

    const expiring = asClient
      ? []
      : data.items
          .filter((i) => !i.archivedAt)
          .map((i) => ({ i, ex: expiryInfo(i) }))
          .filter((x): x is { i: Item; ex: NonNullable<ReturnType<typeof expiryInfo>> } => !!x.ex && x.ex.status !== "ok")
          .sort((a, b) => a.ex.days - b.ex.days);

    return { asClient, vaults, permOf, canSee, canAdd, canEdit, visibleItems, liveItems, cats, requests, waiting, expiring, viewerId };
  }, [data, me.id, isOwner, preview]);
}
